import { createSlice } from '@reduxjs/toolkit';

/**
 * Redux slice for managing real-time updates from Firestore
 * Tracks events by structureId and provides methods to check/clear updates
 */

const initialState = {
  // Map of structureId -> array of events
  events: {},
  // Last update timestamp
  lastUpdate: null,
  // Connection status
  isListening: false,
  // Error state
  error: null,
};

const realtimeSlice = createSlice({
  name: 'realtime',
  initialState,
  reducers: {
    /**
     * Set all events (replaces entire events object)
     */
    setEvents: (state, action) => {
      state.events = action.payload || {};
      state.lastUpdate = new Date().toISOString();
    },

    /**
     * Update events for a specific structureId
     */
    updateStructureEvents: (state, action) => {
      const { structureId, events } = action.payload;
      state.events[structureId] = events;
      state.lastUpdate = new Date().toISOString();
    },

    /**
     * Add a single event to a structureId
     */
    addStructureEvent: (state, action) => {
      const { structureId, event } = action.payload;
      if (!state.events[structureId]) {
        state.events[structureId] = [];
      }
      state.events[structureId].push(event);
      state.lastUpdate = new Date().toISOString();
    },

    /**
     * Clear events for a specific structureId
     * Called after processing updates for that structure
     */
    clearStructureEvents: (state, action) => {
      const structureId = action.payload;
      if (state.events[structureId]) {
        delete state.events[structureId];
      }
    },

    /**
     * Check if a structure has updates
     */
    hasStructureUpdates: (state, action) => {
      const structureId = action.payload;
      return Boolean(state.events[structureId]?.length > 0);
    },

    /**
     * Set listening status
     */
    setListening: (state, action) => {
      state.isListening = action.payload;
    },

    /**
     * Set error state
     */
    setError: (state, action) => {
      state.error = action.payload;
    },

    /**
     * Clear all events (used on logout or business change)
     */
    clearAllEvents: (state) => {
      state.events = {};
      state.lastUpdate = null;
    },

    /**
     * Reset entire state
     */
    resetRealtime: () => initialState,
  },
});

export const {
  setEvents,
  updateStructureEvents,
  addStructureEvent,
  clearStructureEvents,
  hasStructureUpdates,
  setListening,
  setError,
  clearAllEvents,
  resetRealtime,
} = realtimeSlice.actions;

// Selectors
export const selectAllEvents = (state) => state.realtime.events;
export const selectStructureEvents = (structureId) => (state) =>
  state.realtime.events[structureId] || [];
export const selectHasStructureUpdates = (structureId) => (state) =>
  Boolean(state.realtime.events[structureId]?.length > 0);
export const selectIsListening = (state) => state.realtime.isListening;
export const selectLastUpdate = (state) => state.realtime.lastUpdate;
export const selectError = (state) => state.realtime.error;

export default realtimeSlice.reducer;
